<template>
  <v-container fluid class="pa-0">
    <v-row style="background: rgb(var(--v-theme-background))">
      <v-col cols="12">
        <v-alert v-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <v-alert v-if="!matrixData && !loading" type="info" class="mb-4">
          No sales data available. Start by submitting sales records.
        </v-alert>

        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>

        <div v-else-if="matrixData && hasData">
          <!-- Detailed Matrix Table -->
          <v-table class="sticky-table">
            <thead>
              <tr>
                <th>
                  <v-btn
                    icon="mdi-arrow-expand-vertical"
                    @click="expandAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                  <v-btn
                    icon="mdi-arrow-collapse-vertical"
                    @click="collapseAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                </th>
                <th
                  v-for="month in safeArray(matrixData.months)"
                  :key="month"
                  colspan="1"
                  class="text-center"
                >
                  {{ formatMonth(month) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="city in getCitiesWithData()" :key="city">
                <!-- City Header Row -->
                <tr>
                  <td @click="toggleCity(city)" class="pl-2">
                    <v-icon small class="mr-1">
                      {{ isCityExpanded(city) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                    <strong :class="cityColor(city)">{{ city }}</strong>
                  </td>

                  <td
                    v-for="month in safeArray(matrixData.months)"
                    :key="month"
                    class="text-right city-month-average"
                    :class="cityColor(city)"
                  >
                    <strong>{{ formatAmount(getCityMonthAverage(city, month)) }}</strong>
                  </td>
                </tr>

                <!-- Locations for this city -->
                <template v-if="isCityExpanded(city)">
                  <template v-for="location in getLocationsWithData(city)" :key="location">
                    <!-- Location Average Row -->
                    <tr>
                      <td style="padding-left: 1rem" @click="toggleLocation(city, location)">
                        <v-icon small class="mr-1">
                          {{
                            isLocationExpanded(city, location)
                              ? "mdi-chevron-down"
                              : "mdi-chevron-right"
                          }}
                        </v-icon>
                        <span :class="locationColor(location)">{{ location }}</span>
                      </td>
                      <td
                        v-for="month in safeArray(matrixData.months)"
                        :key="month"
                        class="text-right"
                        :class="locationColor(location)"
                      >
                        {{ formatAmount(getCityLocationMonthAverage(city, location, month)) }}
                      </td>
                    </tr>

                    <!-- Brands for this location -->
                    <template v-if="isLocationExpanded(city, location)">
                      <template v-for="brand in getBrandsWithData(city, location)" :key="brand">
                        <!-- Brand Average Row -->
                        <tr>
                          <td
                            style="padding-left: 2rem"
                            @click="toggleBrand(city, location, brand)"
                          >
                            <v-icon small class="mr-1">
                              {{
                                isBrandExpanded(city, location, brand)
                                  ? "mdi-chevron-down"
                                  : "mdi-chevron-right"
                              }}
                            </v-icon>
                            <span :class="brandColor(brand)">{{ brand }}</span>
                          </td>
                          <td
                            v-for="month in safeArray(matrixData.months)"
                            :key="month"
                            class="text-right brand-month-average"
                            :class="brandColor(brand)"
                          >
                            {{
                              formatAmount(
                                getCityLocationBrandMonthAverage(city, location, brand, month),
                              )
                            }}
                          </td>
                        </tr>

                        <!-- Platforms for this brand -->
                        <template v-if="isBrandExpanded(city, location, brand)">
                          <tr
                            v-for="platform in getPlatformsWithData(city, location, brand)"
                            :key="platform"
                          >
                            <td style="padding-left: 3.5rem">
                              {{ platform }}
                            </td>
                            <td
                              v-for="month in safeArray(matrixData.months)"
                              :key="month"
                              class="text-right"
                            >
                              {{
                                formatAmount(
                                  getPlatformMonthData(city, location, brand, platform, month),
                                )
                              }}
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                </template>
              </template>

              <!-- Grand Averages Row -->
              <tr>
                <td><strong>Total Average</strong></td>
                <td v-for="month in safeArray(matrixData.months)" :key="month" class="text-right">
                  <strong>{{ formatAmount(getGrandMonthAverage(month)) }}</strong>
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- Summary Stats -->
          <!-- <v-row class="mb-4" v-if="isDev"> -->
          <!--   <v-col cols="12" md="4"> -->
          <!--     <v-card variant="outlined"> -->
          <!--       <v-card-text class="text-center"> -->
          <!--         <div class="text-h6 text-primary"> -->
          <!--           {{ formatAmount(matrixData.grandAverage) }} -->
          <!--         </div> -->
          <!--         <div class="text-caption">Grand Average (Sum of All Platform Averages)</div> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!--   <v-col cols="12" md="4"> -->
          <!--     <v-card variant="outlined"> -->
          <!--       <v-card-text class="text-center"> -->
          <!--         <div class="text-h6 text-primary">{{ getCitiesWithData().length }}</div> -->
          <!--         <div class="text-caption">Cities with Data</div> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!--   <v-col cols="12" md="4"> -->
          <!--     <v-card variant="outlined"> -->
          <!--       <v-card-text class="text-center"> -->
          <!--         <div class="text-h6 text-primary">{{ getTotalPlatformsWithData() }}</div> -->
          <!--         <div class="text-caption">Active Platform Combinations</div> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!-- </v-row> -->
          <!-- Platform Combinations Summary -->
          <!-- <v-row class="mt-6" v-if="isDev"> -->
          <!--   <v-col cols="12"> -->
          <!--     <v-card variant="outlined"> -->
          <!--       <v-card-title>Platform Combinations</v-card-title> -->
          <!--       <v-card-text> -->
          <!--         <v-list density="compact"> -->
          <!--           <v-list-item -->
          <!--             v-for="combo in getTopPlatformCombinations(20)" -->
          <!--             :key="combo.key" -->
          <!--             class="px-0" -->
          <!--           > -->
          <!--             <v-list-item-title class="text-body-2"> -->
          <!--               {{ combo.city }} - {{ combo.location }} - {{ combo.brand }} - -->
          <!--               {{ combo.platform }} -->
          <!--             </v-list-item-title> -->
          <!--             <v-list-item-subtitle class="text-h6 text-primary font-weight-bold"> -->
          <!--               {{ formatAmount(combo.average) }} -->
          <!--             </v-list-item-subtitle> -->
          <!--           </v-list-item> -->
          <!--         </v-list> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!-- </v-row> -->

          <!-- NET sales note -->
          <div class="text-caption text-disabled text-right mt-2 mr-4">
            Sales are NET from January 2026 onwards
          </div>
        </div>
      </v-col>
    </v-row>

  </v-container>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, reactive, computed, watchEffect } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/api.js";
import { useApp } from "@/composables/useApp.js";
import { useSalesForm } from "@/composables/useSalesForm.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";

const router = useRouter();

const { isDev } = useApp();
const { cityColor, locationColor, brandColor } = useSalesForm();
const { setActions, clearActions } = useAppBarActions();

// Confirm dialog
const { confirm } = useConfirmDialog();

const matrixData = ref(null);
const loading = ref(false);
const error = ref("");
const exporting = ref(false);

// Computed properties for safe data checking
const hasData = computed(() => {
  return (
    matrixData.value &&
    safeArray(matrixData.value.cities).length > 0 &&
    safeArray(matrixData.value.months).length > 0
  );
});

// Expand all rows function
const expandAll = () => {
  if (!matrixData.value) return;

  getCitiesWithData().forEach((city) => {
    expandedCities[city] = true;

    getLocationsWithData(city).forEach((location) => {
      const locationKey = `${city}-${location}`;
      expandedLocations[locationKey] = true;

      getBrandsWithData(city, location).forEach((brand) => {
        const brandKey = `${city}-${location}-${brand}`;
        expandedBrands[brandKey] = true;
      });
    });
  });
};

// Collapse all rows function
const collapseAll = () => {
  // Clear all expanded states
  Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
  Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
  Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);
};

// Safe data access utilities
const safeArray = (arr) => {
  return Array.isArray(arr) ? arr : [];
};

const getSafeValue = (obj, path, defaultValue = 0) => {
  if (!obj) return defaultValue;
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

// Expected combinations lookup from business rules
const expectedLookup = computed(() => {
  const combos = matrixData.value?.expectedCombinations || [];
  return {
    full: new Set(combos),
    cities: new Set(combos.map(c => c.split('|')[0])),
    cityLocations: new Set(combos.map(c => c.split('|').slice(0, 2).join('|'))),
    cityLocationBrands: new Set(combos.map(c => c.split('|').slice(0, 3).join('|'))),
  };
});

// Data filtering functions - show items with actual sales or expected by business rules
const getCitiesWithData = () => {
  if (!matrixData.value?.cities) return [];
  return safeArray(matrixData.value.cities).filter((city) =>
    expectedLookup.value.cities.has(city) ||
    safeArray(matrixData.value.locations).some((location) =>
      safeArray(matrixData.value.brands).some((brand) =>
        safeArray(matrixData.value.platforms).some(
          (platform) =>
            getPlatformMonthData(city, location, brand, platform, matrixData.value.months[0]) > 0,
        ),
      ),
    ),
  );
};

const getLocationsWithData = (city) => {
  if (!matrixData.value?.locations) return [];
  return safeArray(matrixData.value.locations).filter((location) =>
    expectedLookup.value.cityLocations.has(`${city}|${location}`) ||
    safeArray(matrixData.value.brands).some((brand) =>
      safeArray(matrixData.value.platforms).some(
        (platform) =>
          getPlatformMonthData(city, location, brand, platform, matrixData.value.months[0]) > 0,
      ),
    ),
  );
};

const getBrandsWithData = (city, location) => {
  if (!matrixData.value?.brands) return [];
  return safeArray(matrixData.value.brands).filter((brand) =>
    expectedLookup.value.cityLocationBrands.has(`${city}|${location}|${brand}`) ||
    safeArray(matrixData.value.platforms).some(
      (platform) =>
        getPlatformMonthData(city, location, brand, platform, matrixData.value.months[0]) > 0,
    ),
  );
};

const getPlatformsWithData = (city, location, brand) => {
  if (!matrixData.value?.platforms) return [];
  return safeArray(matrixData.value.platforms).filter(
    (platform) =>
      expectedLookup.value.full.has(`${city}|${location}|${brand}|${platform}`) ||
      getPlatformMonthData(city, location, brand, platform, matrixData.value.months[0]) > 0,
  );
};

const getTotalPlatformsWithData = () => {
  let count = 0;
  safeArray(matrixData.value?.cities || []).forEach((city) => {
    safeArray(matrixData.value?.locations || []).forEach((location) => {
      safeArray(matrixData.value?.brands || []).forEach((brand) => {
        safeArray(matrixData.value?.platforms || []).forEach((platform) => {
          if (
            getPlatformMonthData(city, location, brand, platform, matrixData.value.months[0]) > 0
          ) {
            count++;
          }
        });
      });
    });
  });
  return count;
};

// Track expanded/collapsed state
const expandedCities = reactive({});
const expandedLocations = reactive({});
const expandedBrands = reactive({});

const isCityExpanded = (city) => {
  return expandedCities[city] === true;
};

const isLocationExpanded = (city, location) => {
  return expandedLocations[`${city}-${location}`] === true;
};

const isBrandExpanded = (city, location, brand) => {
  return expandedBrands[`${city}-${location}-${brand}`] === true;
};

const toggleCity = (city) => {
  expandedCities[city] = !expandedCities[city];
};

const toggleLocation = (city, location) => {
  const key = `${city}-${location}`;
  expandedLocations[key] = !expandedLocations[key];
};

const toggleBrand = (city, location, brand) => {
  const key = `${city}-${location}-${brand}`;
  expandedBrands[key] = !expandedBrands[key];
};

const formatMonth = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// UPDATED: Use backend-calculated averages
const getPlatformMonthData = (city, location, brand, platform, month) => {
  return getSafeValue(matrixData.value?.data, [city, location, brand, platform, month], 0);
};

// UPDATED: Use backend-calculated location-month averages
const getCityLocationMonthAverage = (city, location, month) => {
  return getSafeValue(matrixData.value?.locationMonthAverages, [city, location, month], 0);
};

// UPDATED: Use backend-calculated city-month averages
const getCityMonthAverage = (city, month) => {
  return getSafeValue(matrixData.value?.cityMonthAverages, [city, month], 0);
};

// UPDATED: Brand-month average - sum of platform averages for that brand
const getCityLocationBrandMonthAverage = (city, location, brand, month) => {
  if (!matrixData.value?.platforms) return 0;
  let total = 0;
  safeArray(matrixData.value.platforms).forEach((platform) => {
    total += getPlatformMonthData(city, location, brand, platform, month);
  });
  return total;
};

// UPDATED: Grand total for month - sum of all city monthly averages
const getGrandMonthAverage = (month) => {
  if (!matrixData.value?.cities) return 0;
  let total = 0;
  safeArray(matrixData.value.cities).forEach((city) => {
    total += getCityMonthAverage(city, month);
  });
  return total;
};

// Get top platform combinations
const getTopPlatformCombinations = (limit = 20) => {
  const combinations = [];

  safeArray(matrixData.value?.cities || []).forEach((city) => {
    safeArray(matrixData.value?.locations || []).forEach((location) => {
      safeArray(matrixData.value?.brands || []).forEach((brand) => {
        safeArray(matrixData.value?.platforms || []).forEach((platform) => {
          // Use the first month's data
          const firstMonth = safeArray(matrixData.value.months)[0];
          const value = firstMonth
            ? getPlatformMonthData(city, location, brand, platform, firstMonth)
            : 0;

          if (value > 0) {
            combinations.push({
              key: `${city}-${location}-${brand}-${platform}`,
              city,
              location,
              brand,
              platform,
              average: value,
            });
          }
        });
      });
    });
  });

  return combinations.sort((a, b) => b.average - a.average).slice(0, limit);
};

const fetchMatrixData = async () => {
  loading.value = true;
  error.value = "";

  try {
    const response = await api.accountingApi.get("/api/accounting/sales/averages");

    matrixData.value = response.data;

    // Reset expanded states
    Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
    Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
    Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);

    // Reset expanded states
    collapseAll();

    // Auto-expand all rows by default
    // expandAll();
  } catch (err) {
    console.error("Failed to load sales averages matrix:", err);
    error.value = err.response?.data?.error || "Failed to load sales averages matrix";
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchMatrixData();
});

// ========== ADD CSV EXPORT FUNCTIONS HERE ==========

const exportToCSV = async () => {
  const dataToExport = flattenAveragesData();

  if (!dataToExport || dataToExport.length === 0) {
    error.value = "No data available to export";
    return;
  }

  const monthsCount = matrixData.value?.months?.length || 0;

  const confirmed = await confirm({
    title: "Export to CSV",
    type: "info",
    htmlMessage: `
      <p>The following data will be exported:</p>
      <ul class="mt-2">
        <li><strong>${dataToExport.length}</strong> rows</li>
        <li><strong>${monthsCount}</strong> months of averages</li>
      </ul>
    `,
    confirmText: "Export",
    cancelText: "Cancel",
  });

  if (!confirmed) return;

  exporting.value = true;
  try {
    generateCSVFile(dataToExport);
  } catch (err) {
    console.error("Export failed:", err);
    error.value = "Failed to export data";
  } finally {
    exporting.value = false;
  }
};

const flattenAveragesData = () => {
  if (!matrixData.value) return [];

  const flattenedData = [];
  const months = safeArray(matrixData.value.months);

  const cities = getCitiesWithData();

  cities.forEach((city) => {
    const locations = getLocationsWithData(city);

    // If no locations, add city month averages
    if (locations.length === 0) {
      flattenedData.push({
        city: city,
        location: "",
        brand: "",
        platform: "",
        ...Object.fromEntries(months.map((month) => [month, getCityMonthAverage(city, month)])),
        total: months.reduce((sum, month) => sum + getCityMonthAverage(city, month), 0),
      });
    } else {
      locations.forEach((location) => {
        const brands = getBrandsWithData(city, location);

        // If no brands, add location-month averages
        if (brands.length === 0) {
          flattenedData.push({
            city: city,
            location: location,
            brand: "",
            platform: "",
            ...Object.fromEntries(
              months.map((month) => [month, getCityLocationMonthAverage(city, location, month)]),
            ),
            total: months.reduce(
              (sum, month) => sum + getCityLocationMonthAverage(city, location, month),
              0,
            ),
          });
        } else {
          brands.forEach((brand) => {
            const platforms = getPlatformsWithData(city, location, brand);

            // If no platforms, add brand-month averages
            if (platforms.length === 0) {
              flattenedData.push({
                city: city,
                location: location,
                brand: brand,
                platform: "",
                ...Object.fromEntries(
                  months.map((month) => [
                    month,
                    getCityLocationBrandMonthAverage(city, location, brand, month),
                  ]),
                ),
                total: months.reduce(
                  (sum, month) =>
                    sum + getCityLocationBrandMonthAverage(city, location, brand, month),
                  0,
                ),
              });
            } else {
              platforms.forEach((platform) => {
                // Add platform detail row
                flattenedData.push({
                  city: city,
                  location: location,
                  brand: brand,
                  platform: platform,
                  ...Object.fromEntries(
                    months.map((month) => [
                      month,
                      getPlatformMonthData(city, location, brand, platform, month),
                    ]),
                  ),
                  total: months.reduce(
                    (sum, month) =>
                      sum + getPlatformMonthData(city, location, brand, platform, month),
                    0,
                  ),
                });
              });
            }
          });
        }
      });
    }
  });

  return flattenedData;
};

const generateCSVFile = (data) => {
  if (!data.length) return;

  const months = safeArray(matrixData.value.months);

  // Define headers - FLAT STRUCTURE
  const headers = [
    "City",
    "Location",
    "Brand",
    "Platform",
    ...months.map((month) => formatMonthForCSV(month)),
    "Total",
  ];

  // Convert data to CSV rows
  const csvRows = [
    headers.join(","),
    ...data.map((item) =>
      [
        escapeCSV(item.city),
        escapeCSV(item.location),
        escapeCSV(item.brand),
        escapeCSV(item.platform),
        ...months.map((month) => formatAmountForCSV(item[month] || 0)),
        formatAmountForCSV(item.total),
      ].join(","),
    ),
  ];

  // Create CSV content
  const csvContent = csvRows.join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `sales_averages_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeCSV = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const formatMonthForCSV = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1);
  // Remove commas from the date format
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" }).replace(/,/g, "");
};

const formatAmountForCSV = (amount) => {
  // Use system locale for proper decimal formatting
  const number = typeof amount === "number" ? amount : parseFloat(amount) || 0;
  return new Intl.NumberFormat(navigator.language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false, // Don't use thousand separators
  }).format(number);
};

// App bar actions
watchEffect(() => {
  setActions([
    {
      icon: "mdi-file-delimited-outline",
      tooltip: "Export CSV",
      onClick: exportToCSV,
      loading: exporting.value,
    },
    {
      icon: "mdi-chart-bar",
      tooltip: "Chart",
      onClick: () => router.push("/admin/charts/sales/monthly-averages"),
    },
  ]);
});
onBeforeUnmount(() => clearActions());
</script>

<style scoped>
.sticky-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  background: rgb(var(--v-theme-background));
}

.sticky-table :deep(thead tr th:first-child),
.sticky-table :deep(tbody tr td:first-child),
.sticky-table :deep(tfoot tr td:first-child) {
  position: sticky;
  left: 0;
  z-index: 2;
  background: rgb(var(--v-theme-background));
  min-width: 175px !important;
  width: 175px !important;
}

.sticky-table :deep(thead tr th:first-child) {
  z-index: 3;
}

.sticky-table :deep(tfoot tr td:first-child) {
  z-index: 3;
}

.grand-averages-row {
  font-weight: bold;
  background-color: color-mix(in srgb, rgb(var(--v-theme-primary)) 5%, transparent);
}

.platform-indent {
  color: rgba(0, 0, 0, 0.6);
}
</style>
